import React, { useState } from 'react';
import { Container, Card, Button, Table, Alert } from 'react-bootstrap';

const SimpleFieldTest = () => {
  const [testResults, setTestResults] = useState(null);

  // Sample data that mimics what comes from PDF extraction
  const sampleData = {
    "products": [
      {
        "product_id": "1",
        "name": "Goldbears",
        "product_information": {
          "Retail Pkg. UPC": "0 42238 30241 9",
          "Country of Origin": "Germany",
          "Retail Pkg. Dimension (L/DxWxH)": "7.9\" x 5.7\" x 0.5\"",
          "Shelf Life": "24 months",
          "Retail Pkg. Net Wt.": "4oz (113g)",
          "Product Type": "Each"
        }
      },
      {
        "product_id": "2", 
        "name": "Twin Snakes",
        "product_information": {
          "Retail Pkg. UPC": "0 42238 34093 0",
          "Country of Origin": "Turkey",
          "Retail Pkg. Dimension (L/DxWxH)": "7.9\" x 5.7\" x 0.5\"",
          "Shelf Life": "24 months",
          "Product Type": "Each"
        }
      }
    ]
  };

  // Simulate the categorizeField function from DynamicForm
  const categorizeField = (fieldName) => {
    const lowerFieldName = fieldName.toLowerCase().trim();
    
    const fieldCategoryMapping = {
      microbiological_properties: [
        'total plate count', 'yeast and mold', 'plate count'
      ],
      product_claims_certifications: [
        'organic', 'non-gmo', 'kosher', 'halal', 'certified'
      ],
      physical_chemical_attributes: [
        'ph', 'moisture', 'color', 'texture', 'appearance'
      ],
      nutritional_information: [
        'calories', 'protein', 'fat', 'carbohydrate'
      ]
    };

    // Check for category matches
    for (const [category, keywords] of Object.entries(fieldCategoryMapping)) {
      if (keywords.some(keyword => lowerFieldName.includes(keyword))) {
        return category;
      }
    }

    return null; // Return null if no category match found
  };

  // Simulate the reorganization logic
  const testReorganization = () => {
    const results = {
      originalFields: [],
      categorizations: [],
      finalFields: [],
      countryOfOriginStatus: 'NOT_FOUND'
    };

    sampleData.products.forEach((product, productIndex) => {
      const productResult = {
        productIndex,
        productName: product.name,
        originalFields: Object.keys(product.product_information || {}),
        fieldAnalysis: []
      };

      // Analyze each field
      Object.entries(product.product_information || {}).forEach(([fieldName, fieldValue]) => {
        const category = categorizeField(fieldName);

        // Check for character encoding issues
        const charCodes = fieldName.split('').map(char => char.charCodeAt(0));
        const hasSpecialChars = charCodes.some(code => code > 127);

        const analysis = {
          fieldName,
          fieldValue,
          category: category || 'STAYS_IN_PRODUCT_INFORMATION',
          isCountryOfOrigin: fieldName === "Country of Origin",
          charCodes: charCodes,
          hasSpecialChars: hasSpecialChars,
          fieldLength: fieldName.length
        };

        productResult.fieldAnalysis.push(analysis);

        if (fieldName === "Country of Origin") {
          results.countryOfOriginStatus = 'FOUND';
          console.log('Country of Origin field analysis:', analysis);
        }
      });

      results.originalFields.push(productResult);
    });

    setTestResults(results);
  };

  return (
    <Container className="mt-4">
      <Card>
        <Card.Header>
          <h4>Simple Field Test - Country of Origin Analysis</h4>
        </Card.Header>
        <Card.Body>
          <p>This test analyzes how the Country of Origin field is processed without the full DynamicForm complexity.</p>
          
          <Button onClick={testReorganization} className="mb-3">
            Run Field Analysis
          </Button>

          {testResults && (
            <div>
              <Alert variant={testResults.countryOfOriginStatus === 'FOUND' ? 'success' : 'danger'}>
                <h6>Country of Origin Status: {testResults.countryOfOriginStatus}</h6>
              </Alert>

              {testResults.originalFields.map((product, index) => (
                <Card key={index} className="mb-3">
                  <Card.Header>
                    <h6>Product {product.productIndex + 1}: {product.productName}</h6>
                  </Card.Header>
                  <Card.Body>
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Field Name</th>
                          <th>Field Value</th>
                          <th>Category</th>
                          <th>Length</th>
                          <th>Special Chars?</th>
                          <th>Is Country of Origin?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.fieldAnalysis.map((field, fieldIndex) => (
                          <tr key={fieldIndex} style={{
                            backgroundColor: field.isCountryOfOrigin ? '#d4edda' : 'transparent'
                          }}>
                            <td>
                              <strong>{field.fieldName}</strong>
                              {field.isCountryOfOrigin && (
                                <div className="small text-muted">
                                  Char codes: [{field.charCodes.join(', ')}]
                                </div>
                              )}
                            </td>
                            <td>{field.fieldValue}</td>
                            <td>{field.category}</td>
                            <td>{field.fieldLength}</td>
                            <td>{field.hasSpecialChars ? '⚠️ YES' : '✅ No'}</td>
                            <td>{field.isCountryOfOrigin ? '✅ YES' : '❌ No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SimpleFieldTest;
